import React from 'react';
import { AlertCircle, Loader2, Building2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import OtpChallengePanel from '@/modules/self-register/components/OtpChallengePanel';
import { IITH, SERIF, SANS } from '@/pages/landing/iithBrand';

export default function LoginFormPanel({
  chrome, brandColor, useBranded, singleClinic,
  error, otpChallenge, loading,
  userId, setUserId, password, setPassword,
  isStaff, isPasswordRequired, passwordPlaceholder, isOtpLogin,
  staffIdMode, onToggleStaffMode,
  handleSubmit, handleOtpVerify, onOtpBack,
  slug, setSlug, tenant, availableTenants, filteredTenants,
  tenantQuery, setTenantQuery,
}) {
  const accent = brandColor || IITH.saffron;
  const Icon = chrome.icon;

  return (
    <div className="w-full max-w-[420px] mx-auto animate-[loginCardIn_0.65s_ease-out]" data-testid="login-form-panel">
      <div className="lg:hidden mb-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.28em] mb-2" style={{ color: IITH.gold, fontFamily: SANS }}>
          {IITH.institute}
        </p>
        <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: SERIF }}>
          {IITH.hospital}
        </h1>
      </div>

      <div className="rounded-2xl bg-white/95 backdrop-blur-md shadow-2xl shadow-black/25 border border-white/60 overflow-hidden">
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${IITH.gold})` }} />
        <div className="px-6 sm:px-8 pt-7 pb-8">
          <div className="flex items-start gap-3 mb-6">
            <div
              className="h-11 w-11 rounded-xl grid place-items-center text-white shrink-0"
              style={{ background: `linear-gradient(145deg, ${IITH.navyMid}, ${IITH.navy})` }}
              data-testid="login-brand-logo"
            >
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h2
                className="text-xl font-semibold text-[#071B34]"
                style={{ fontFamily: SERIF }}
                data-testid="login-detected-role-title"
              >
                {chrome.title}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5" style={{ fontFamily: SANS }}>{chrome.subtitle}</p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
            </div>
          )}

          <div className="space-y-2 mb-4" hidden={useBranded || singleClinic}>
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5 text-zinc-500" /> Clinic / Tenant
            </Label>
            {availableTenants.length > 0 ? (
              <Select value={slug || ''} onValueChange={setSlug}>
                <SelectTrigger className="h-12" data-testid="login-tenant-select">
                  <SelectValue placeholder={`Select clinic (${availableTenants.length})`} />
                </SelectTrigger>
                <SelectContent>
                  <div className="sticky top-0 bg-white border-b p-2 z-10"
                    onKeyDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                      <Input value={tenantQuery} onChange={(e) => setTenantQuery(e.target.value)}
                        placeholder="Search…" className="pl-8 h-8 text-xs" data-testid="login-tenant-search" />
                    </div>
                  </div>
                  {filteredTenants.map((t) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      <span className="block">{t.name}</span>
                      <span className="block text-[10px] text-zinc-500 font-mono">{t.slug}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input value={slug || ''} onChange={(e) => setSlug(e.target.value)}
                className="h-12" data-testid="login-tenant-input" />
            )}
            {tenant?.status === 'suspended' && (
              <p className="text-[11px] text-red-600">This tenant is currently suspended.</p>
            )}
          </div>

          {otpChallenge ? (
            <OtpChallengePanel
              challenge={otpChallenge}
              verifying={loading}
              accent={accent}
              onVerify={handleOtpVerify}
              onBack={onOtpBack}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="doctor-id">{chrome.idLabel}</Label>
                <Input id="doctor-id" value={userId} onChange={(e) => setUserId(e.target.value)}
                  placeholder={chrome.idPlaceholder} className="h-12 text-base" required autoFocus
                  data-testid="login-doctor-id" />
                {isStaff && (
                  <p className="text-[11px] text-zinc-500" data-testid="login-staff-hint">
                    Detected as <b>{chrome.title}</b>
                  </p>
                )}
              </div>
              {!isOtpLogin && (
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder={passwordPlaceholder}
                    className="h-12 text-base" required={isPasswordRequired} data-testid="login-password" />
                </div>
              )}
              {isOtpLogin && (
                <p className="text-[12px] text-zinc-500 leading-relaxed" data-testid="login-otp-hint">
                  Enter your registered email. We will send a one-time password to that inbox.
                </p>
              )}
              <Button type="submit" disabled={loading || !userId.trim()}
                className="w-full h-12 text-base text-white font-semibold tracking-wide"
                style={{ background: accent, fontFamily: SANS }} data-testid="login-submit">
                {loading
                  ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />{isOtpLogin ? 'Validating…' : 'Signing in…'}</>
                  : (isOtpLogin ? 'Continue' : 'Continue securely')}
              </Button>
            </form>
          )}

          {useBranded && typeof onToggleStaffMode === 'function' && !otpChallenge && (
            <button
              type="button"
              onClick={onToggleStaffMode}
              className="mt-4 w-full text-center text-[11px] text-zinc-400 hover:text-zinc-600"
              data-testid="login-toggle-staff-mode"
            >
              {staffIdMode ? '← Back to email OTP sign in' : 'Staff ID login (pharmacy / stores)'}
            </button>
          )}

          {useBranded ? (
            <p className="text-center mt-4 text-xs text-zinc-500" data-testid="login-register-link">
              New to the hospital?{' '}
              <a href="/register" className="font-semibold hover:underline" style={{ color: accent }}>
                Create a campus account
              </a>
            </p>
          ) : (
            <p className="text-center mt-5 text-xs text-zinc-500" data-testid="login-signup-link">
              Don&apos;t have a clinic?{' '}
              <a href="/signup" className="font-semibold text-blue-700 hover:underline">Create one →</a>
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes loginCardIn {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
